import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Link2, CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const VerifyEmailPage = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('loading'); // loading, success, already_verified, error
  const [error, setError] = useState('');
  
  const token = searchParams.get('token');

  const t = {
    es: {
      verifying: 'Verificando tu cuenta...',
      success: '¡Cuenta Verificada!',
      successMessage: 'Tu cuenta ha sido verificada correctamente. Ya puedes iniciar sesión.',
      alreadyVerified: 'Cuenta ya verificada',
      alreadyVerifiedMessage: 'Tu email ya ha sido verificado anteriormente.',
      error: 'Error de Verificación',
      errorMessage: 'El enlace de verificación es inválido o ha expirado.',
      noToken: 'No se proporcionó token de verificación.',
      goToLogin: 'Iniciar Sesión',
      requestNew: 'Solicitar nuevo enlace',
    },
    en: {
      verifying: 'Verifying your account...',
      success: 'Account Verified!',
      successMessage: 'Your account has been verified successfully. You can now sign in.',
      alreadyVerified: 'Already verified',
      alreadyVerifiedMessage: 'Your email has already been verified.',
      error: 'Verification Error',
      errorMessage: 'The verification link is invalid or has expired.',
      noToken: 'No verification token provided.',
      goToLogin: 'Sign In',
      requestNew: 'Request new link',
    },
  };

  const texts = t[language] || t.es;

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setError(texts.noToken);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/verify-email?token=${token}`);
        const data = await response.json();

        if (response.ok) {
          if (data.already_verified) {
            setStatus('already_verified');
          } else {
            setStatus('success');
          }
        } else {
          setStatus('error');
          setError(data.detail || texts.errorMessage);
        }
      } catch (err) {
        setStatus('error');
        setError(texts.errorMessage);
      }
    };

    verifyEmail();
  }, [token, texts.noToken, texts.errorMessage]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <CardContent className="text-center py-8">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">{texts.verifying}</p>
          </CardContent>
        );

      case 'success':
        return (
          <>
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <CardTitle className="text-2xl font-display text-green-600">
                {texts.success}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">{texts.successMessage}</p>
              <Button onClick={() => navigate('/login')} className="w-full">
                {texts.goToLogin}
              </Button>
            </CardContent>
          </>
        );

      case 'already_verified':
        return (
          <>
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Mail className="h-10 w-10 text-blue-600" />
              </div>
              <CardTitle className="text-2xl font-display text-blue-600">
                {texts.alreadyVerified}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">{texts.alreadyVerifiedMessage}</p>
              <Button onClick={() => navigate('/login')} className="w-full">
                {texts.goToLogin}
              </Button>
            </CardContent>
          </>
        );

      case 'error':
        return (
          <>
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <CardTitle className="text-2xl font-display text-red-600">
                {texts.error}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <div className="flex flex-col gap-2">
                <Button onClick={() => navigate('/login')} className="w-full">
                  {texts.goToLogin}
                </Button>
                <Button onClick={() => navigate('/register')} variant="outline" className="w-full">
                  {texts.requestNew}
                </Button>
              </div>
            </CardContent>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-subtle-gradient p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Link2 className="h-6 w-6" />
          </div>
          <span className="font-display text-2xl font-bold text-foreground">
            Puro<span className="text-primary">link</span>
          </span>
        </Link>

        <Card className="shadow-xl border-border/60">
          {renderContent()}
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
